import type { IExternalLineBaseData, IPoleBaseData } from '$lib/models/IBaseDataModel';
import type { IDistanceAnalysis } from '$lib/models/IDistanceAnalysis';
import type { IPoleReportModel } from '$lib/models/IPoleReportModel';
import {
	useMutation,
	useQuery,
	type UseMutationOptions,
	type UseQueryOptions
} from '@sveltestack/svelte-query';
import type { IGeoJsonPolygonModel } from '../models/IGeoJsonPolygonModel';
import { BaseDataService } from '../services/base-data-service';
import { CompanyService } from '../services/company-service';
import api from '../services/kyAuthenticatedInstance';
import type { INewLinesPayload } from '$lib/models/INewLinesPayload';
import ky, { HTTPError, type ResponsePromise } from 'ky';
import type { IPoleImage } from '$lib/models/IPoleImage';
import { get } from 'svelte/store';
import { getPlanQueryParameter, mapModeStore } from '$lib/core/map/map-mode-store';
import type { APIErrorModel } from '$lib/models/APIErrorModel';
import type { IMastpointDataModel } from '$lib/models/IMastPointFullModel';
import type { ISmartData } from '$lib/models/ISmartData';
import type { IMastPointImage } from '$lib/models/IMastPointImage';
import {
	fromFileManagerImage,
	fromMastPointFile,
	newestFirst
} from '$lib/utils/mastpoint-image-adapters';

/**
 * Fetches a page of the image list. Exported so callers can re-run it with an
 * updated payload via svelte-query's `setOptions` (reactive filters/pagination)
 * without duplicating the endpoint.
 */
export const fetchImageList = (payload: ISmartData.FileManagerListRequest) =>
	api.post('FileManager/List', { json: payload }).json<ISmartData.ImageList>();

/** Free-text image search (capped, relevance-ordered — see ImageSearchRequest). */
export const fetchImageSearch = (payload: ISmartData.ImageSearchRequest) =>
	api.post('FileManager/Search', { json: payload }).json<ISmartData.ImageSearchResult>();

/** Uploads registered via `CreateUploadUrl` that never reached `FinalizeUpload`. */
export const fetchPendingUploads = () =>
	api.get('FileManager/PendingUploads').json<ISmartData.PendingUploadList>();

/** Stable key for the mastpoint image query, shared by the hook and `updateOptions`. */
export const mastPointImagesQueryKey = (mastPointId: number, conditionId?: number | null) => [
	'useMastPointImages',
	mastPointId,
	conditionId ?? null
];

/**
 * Defaults for the mastpoint image query.
 *
 * Deliberately no `staleTime`, so opening the gallery always refetches: image URLs
 * are time-limited SAS tokens and stale ones start returning 403. Do NOT add
 * `refetchOnWindowFocus` — a fresh SAS token means a new query string, which is a
 * new cache key to the browser, so every tab refocus would re-download every
 * thumbnail.
 */
export const mastPointImagesQueryOptions: UseQueryOptions<IMastPointImage[], HTTPError> = {
	refetchInterval: false,
	retry: 1,
	refetchIntervalInBackground: false,
	refetchOnMount: true,
	refetchOnReconnect: false,
	refetchOnWindowFocus: false
};

/**
 * Every image and file attached to a mastpoint, normalized across both storage
 * backends. Exported so callers can re-run it through `updateOptions` once the
 * company's migration flag resolves — mirroring `fetchImageList` above.
 *
 * `imagesMigrated` comes from `IGlobalBaseDataModel.imageMigrationComplete`. The
 * legacy endpoint is backwards-compatible and serves both storages, so `false` is
 * always a safe answer — it just cannot supply compressed previews.
 */
export const fetchMastPointImages = async ({
	mastPointId,
	conditionId,
	imagesMigrated
}: {
	mastPointId: number;
	conditionId?: number | null;
	imagesMigrated: boolean;
}): Promise<IMastPointImage[]> => {
	if (imagesMigrated) {
		// Unpaged — a mast has few images. The endpoint takes no conditionId filter,
		// so narrow client-side instead of asking the backend for one.
		const images = await api
			.get(`FileManager/mast/${mastPointId}`)
			.json<ISmartData.ImageItem[]>();
		const mapped = images.map(fromFileManagerImage);

		return newestFirst(
			conditionId ? mapped.filter((image) => image.conditionId === conditionId) : mapped
		);
	}

	const conditionFilter = conditionId ? `?conditionId=${conditionId}` : '';
	const images = await api
		.get(`MastPointFile/${mastPointId}${conditionFilter}`)
		.json<IPoleImage[]>();

	return newestFirst(images.map(fromMastPointFile));
};

const mastpoint = {
	useGetMastpointCzmlQuery: <T>({
		mastPointId,
		queryOptions
	}: {
		mastPointId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) => {
		return useQuery<T, HTTPError, T>(
			['useGetMastpointCzmlQuery', mastPointId],
			async () => api.get(`MastPoint/${mastPointId}/czml`).json<T>(),
			{ ...queryOptions }
		);
	},
	useGetMastpointDataQuery: <T>({
		mastpointId,
		queryOptions
	}: {
		mastpointId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) => {
		return useQuery<T, HTTPError, T>(
			['useGetMastpointDataQuery', mastpointId],
			async () => api.get(`MastPoint/${mastpointId}/data`).json<T>(),
			{ ...queryOptions }
		);
	},
	mastPointPositionCommandOptions: <T>({
		mastpointId,
		queryOptions
	}: {
		mastpointId?: number | string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['mastPointPositionCommandOptions', mastpointId],
			async () => api.get(`MastPoint/UpdatePosition/${mastpointId}/options`).json<T>(),
			{ ...queryOptions }
		),
	mastPointConstructionTemplateQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['mastpointConstructionTemplateQuery'],
			async () => api.get(`MastPoint/constructions`).json<T>(),
			{ ...queryOptions }
		),
	// Live per-load-case analysis (mast FEM, 5 ILcs + autodesign per ILc). A heavier call than
	// calculations/data - the caller runs it when the analysis panel (the tab) mounts, with
	// cacheTime/staleTime 0 so the numbers are always fresh.
	useGetMastLoadCaseAnalysisQuery: <T>({
		mastpointId,
		queryOptions
	}: {
		mastpointId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['mastLoadCaseAnalysisQuery', mastpointId],
			async () => api.get(`MastPoint/${mastpointId}/loadcaseanalysis`).json<T>(),
			{ ...queryOptions }
		),
	// Damage report for the whole mastpoint (method 2a - FEM stress): every pole is run both
	// WITH and WITHOUT the registered damages, for every load case. Twice as heavy as
	// loadcaseanalysis above, so it only runs when the report route mounts - and with
	// cacheTime/staleTime 0 so the numbers are never served from cache.
	useMastDamageReportQuery: <T>({
		mastpointId,
		queryOptions
	}: {
		mastpointId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['mastDamageReportQuery', mastpointId],
			async () => api.get(`MastPoint/${mastpointId}/damagereport`).json<T>(),
			{ ...queryOptions }
		)
};

const utilityLinks = {
	useGetUtilityLinkQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) => {
		return useQuery<T, HTTPError, T>(
			['useGetUtilityLinkQuery'],
			async () => api.get(`UtilityLink/constructions`).json<T>(),
			{ ...queryOptions }
		);
	},
	useGetUtilityLinkBaseDataQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) => {
		return useQuery<T, HTTPError, T>(
			['useGetUtilityLinkBaseDataQuery'],
			async () => api.get(`UtilityLinkBaseData`).json<T>(),
			{ ...queryOptions }
		);
	}
};

const cable = {
	// Temporary mock request
	useCableBaseDataQuery: <T>({ queryOptions }: { queryOptions?: UseQueryOptions<T, HTTPError> }) =>
		useQuery<T, HTTPError, T>(
			['useCableBaseDataQuery'],
			async () => api.get('CableBaseData').json<T>(),
			queryOptions
		)
};
const conditions = {
	useFormBaseDataQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			'useFormBaseDataQuery',
			async () => api.get('ConditionReport/Templates/Categories').json<T>(),
			{ ...queryOptions }
		),

	useExistingTemplatesQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError>(
			'useExistingTemplatesQuery',
			async () => api.get('ConditionReport/Templates').json<T>(),
			{ ...queryOptions }
		),
	useConditionsSpecificationsQuery: <T>({
		templateType,
		queryOptions
	}: {
		templateType: 'LowVoltageTemplate' | 'HighVoltageTemplate';
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useConditionsSpecificationsQuery',
			async () => {
				const czmlQueryParameters = get(getPlanQueryParameter);
				const templateTypeParameter = czmlQueryParameters.length
					? '&templateType=' + templateType
					: '?templateType=' + templateType;
				return api.get('Condition/spec' + czmlQueryParameters + templateTypeParameter).json<T>();
			},
			{ ...queryOptions }
		),
	useAllConditionsForMastpointQuery: <T>({
		mastPointId,
		queryOptions
	}: {
		mastPointId?: number | string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['useAllConditionsForMastpointQuery', mastPointId],
			async () => api.get(`Condition/all/${mastPointId}`).json<T>(),
			{ ...queryOptions }
		),
	useComponentsConditionStatus: <T>({
		mastPointId,
		queryOptions
	}: {
		mastPointId?: number | string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['useComponentsConditionStatus', mastPointId],
			async () => api.get(`Condition/components/${mastPointId}`).json<T>(),
			{ ...queryOptions }
		),
	useConditionsSpecificationForPlanQuery: <T>({
		planId,
		queryOptions
	}: {
		planId?: number | string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['useConditionsSpecificationForPlanQuery', planId],
			async () => api.get(`Plans/template/${planId}`).json<T>(),
			{ ...queryOptions }
		),
	// Plan-level condition report: all active (unrepaired) deviations in the plan area with
	// STORED numbers - no calculation in the call. The caller sets cacheTime/staleTime 0 and
	// gates `enabled` on a valid planId.
	usePlanConditionReportQuery: <T>({
		planId,
		queryOptions
	}: {
		planId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError>(
			['usePlanConditionReportQuery', planId],
			async () => api.get(`ConditionReport/plan/${planId}`).json<T>(),
			{ ...queryOptions }
		),
	useExistingMastPointConditionReportsQuery: <T>({
		mastPointId,
		projectId,
		queryOptions
	}: {
		mastPointId?: number | string;
		projectId?: number | string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useExistingMastPointConditionReports', mastPointId, projectId],
			async () =>
				api
					.get(`MastPoint/${mastPointId}/projects/${projectId}/ConditionReports`, {
						retry: 0
					})
					.json<T>(),
			queryOptions
		)
};

// These are POSTs (still a query not a mutation) because of the required input structure of unknown size (may not fit in the URL query)
const reports = {
	usePolygonCapacityReportQuery: <T>() =>
		useMutation<T, APIErrorModel, { polygon: IGeoJsonPolygonModel }>(
			'usePolygonCapacityReportQuery',
			({ polygon }) =>
				api
					.post(`Reports/Data/CapacityReport`, {
						json: polygon
					})
					.json()
		),

	usePoleReportQuery: <T = IPoleReportModel.PoleReport>(
		poleId: number,
		queryOptions?: UseQueryOptions<T, HTTPError>
	) =>
		useQuery<T, HTTPError>(
			['usePoleReportQuery', poleId],
			() => api.get(`Reports/pole/${poleId}`).json<T>(),
			queryOptions
		),

	usePolygonCapacityReportExcelFileQuery: () =>
		useMutation<Blob, HTTPError, { polygon: IGeoJsonPolygonModel['polygon'] }>(
			'usePolygonCapacityReportExcelFileQuery',
			({ polygon }) =>
				api
					.post(`Reports/File/CapacityReport`, {
						json: {
							polygon
						}
					})
					.blob()
		),
	useNewCapacityReportExcelFileQuery: () =>
		useMutation<
			Blob,
			HTTPError,
			{ newCapacityReportsData: Partial<INewLinesPayload.InputLineGroup[]> }
		>('useNewCapacityReportExcelFileQuery', ({ newCapacityReportsData }) =>
			api
				.post(`Reports/File/NewCapacityReport` + get(getPlanQueryParameter), {
					json: newCapacityReportsData
				})
				.blob()
		)
};

const projects = {
	useProjectsQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			'useProjectsQuery',
			async () => api.get('Projects').json<T>(),
			queryOptions
		),
	useProjectInfoPlansQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			'useProjectInfoPlansQuery',
			async () => api.get('Projects/info/plans').json<T>(),
			queryOptions
		),

	usePlansByProjectQuery: <T>(
		projectId: number | undefined,
		queryOptions?: UseQueryOptions<T, HTTPError>
	) =>
		useQuery<T, HTTPError, T>(
			['usePlansByProjectQuery', projectId],
			async () => api.get(`Projects/${projectId ? projectId : null}/plans`).json<T>(),
			queryOptions
		),
	useProjectPolesQuery: <T>({
		projectId,
		queryOptions
	}: {
		projectId?: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useSingleProjectPolesQuery', projectId],
			async () => api.get(`Projects/${projectId}/Poles`).json<T>(),
			queryOptions
		),

	useProjectTypesQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			'useProjectTypesQuery',
			async () => api.get(`Projects/ProjectTypes`).json<T>(),
			queryOptions
		),

	useProjectMastpointsQuery: <T>({
		projectId,
		queryOptions
	}: {
		projectId?: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useSingleProjectMastpointsQuery', projectId],
			async () => api.get(`Projects/${projectId}/MastPoints`).json<T>(),
			queryOptions
		),
	useGetPlanAttachmentsQuery: <T>({
		planId,
		queryOptions
	}: {
		planId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useGetPlanAttachmentsQuery',
			async () => api.get(`Plans/attachments/${planId}`).json<T>(),
			queryOptions
		),
	useGetPlanDrawingsQuery: <T>({
		planId,
		queryOptions
	}: {
		planId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useGetPlanDrawingsQuery',
			async () => api.get(`Plans/drawings/all/${planId}`).json<T>(),
			queryOptions
		),
	useGetPlanClimateLoadsQuery: <T>({
		planId,
		queryOptions
	}: {
		planId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useGetPlanClimateLoadsQuery', planId],
			async () => api.get(`Plans/${planId}/climateloads`).json<T>(),
			queryOptions
		)
};

const poles = {
	useMastCalcAnalysisQuery: <T>({
		poleId,
		queryOptions
	}: {
		poleId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useMastCalcAnalysisQuery',
			async () =>
				api
					.get(`MastPoint/mastCalc/${poleId}`, {
						retry: {
							limit: 0
						}
					})
					.json<T>(),
			queryOptions
		)
};

// These are POSTs (still a query not a mutation) because of the required input structure of unknown size (may not fit in the URL query)
const distanceAnalysis = {
	useDistanceAnalysisQuery: <T>() =>
		useMutation<T, HTTPError, { distanceAnalysisBody: IDistanceAnalysis }>(
			'useDistanceAnalysisQuery',
			({ distanceAnalysisBody }) =>
				api
					.post(`Lines/LineTerrainProfile/czml`, {
						json: distanceAnalysisBody
					})
					.json()
		)
};

const companiesAndUsers = {
	useSingleCompanyUsersQuery: <T>({
		companyId,
		queryOptions
	}: {
		companyId?: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useCompanyUsersQuery', companyId],
			async () => api.get(`Companies/${companyId}/Users`).json<T>(),
			queryOptions
		),

	useSingleUserQuery: <T>({
		userId,
		queryOptions
	}: {
		userId?: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useUserQuery', userId],
			async () => api.get(`Account/${userId}`).json<T>(),
			queryOptions
		),

	useCompaniesQuery: <T>({ queryOptions }: { queryOptions?: UseQueryOptions<T, HTTPError> }) =>
		useQuery<T, HTTPError, T>(
			['useCompaniesQuery'],
			async () => await CompanyService.getCompanies(),
			queryOptions
		),
	useAllPermissionsQuery: <T>({ queryOptions }: { queryOptions?: UseQueryOptions<T, HTTPError> }) =>
		useQuery<T, HTTPError, T>(
			['useAllPermissionsQuery'],
			async () => await api.get(`Account/Permissions`).json<T>(),
			queryOptions
		),
	useSingleCompanyQuery: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number | undefined;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useSingleCompanyQuery', companyId],
			async () => await api.get(`Companies/${companyId}`).json<T>(),
			queryOptions
		),
	useRolesQuery: <T>({ queryOptions }: { queryOptions?: UseQueryOptions<T, HTTPError> }) =>
		useQuery<T, HTTPError, T>(
			['useRolesQuery'],
			async () => await CompanyService.getRoles(),
			queryOptions
		),

	useCompanyPartnershipsQuery: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number | undefined;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useCompanyPartnershipsQuery', companyId],
			async () => await api.get(`Companies/${companyId}/companypartnership`).json<T>(),
			queryOptions
		),
	useBrregQuery: <T>() =>
		useMutation<T, HTTPError, { orgNr: string }>('useBrregQuery', async ({ orgNr }) => {
			const brregResponse = await ky
				.get(`https://data.brreg.no/enhetsregisteret/api/enheter/${orgNr}`)
				.json<T>();
			return brregResponse;
		}),
	useGetCompanySettingsQuery: <T>({ companyId }: { companyId: number }) =>
		useQuery<T, HTTPError, T>(['useGetCompanySettingsQuery', companyId], async () =>
			api.get(`Companies/settings/${companyId}`).json<T>()
		)
};

const aviationObstacleData = {
	useAllAviationObstacleQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useAllAviationObstacleQuery'],
			async () => await api.get('AviationObstacles/all').json<T>(),
			queryOptions
		),
	useAviationObstacleGeoJsonQuery: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useAviationObstacleGeoJsonQuery',
			async () => await api.get(`AviationObstacles/allGeoJson?companyId=${companyId}`).json<T>(),
			queryOptions
		),
	useAviationObstacleNrlDisconnected: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useAviationObstacleNrlDisconnectedQuery'],
			async () =>
				await api.get(`AviationObstacles/NrlAndDisconnectedData?companyId=${companyId}`).json<T>(),
			queryOptions
		),
	useSingleAviationObstacleQuery: <T>({
		entityId,
		queryOptions
	}: {
		entityId?: string;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			['useSingleAviationObstacleQuery', entityId],
			async () => api.get(`AviationObstacles/${entityId}`).json<T>(),
			queryOptions
		),
	useGetAllAviationObstaclesAsGeojson: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useGetAllAviationObstaclesAsGeojson',
			async () => api.get(`AviationObstacles/allGeoJson?companyId=${companyId}`).json<T>(),
			queryOptions
		),
	useAviationObstacleSyncReportsQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useAviationObstacleSyncReportsQuery',
			async () => api.get(`AviationObstacles/syncreports`).json<T>(),
			queryOptions
		),
	useCheckIsNrlSyncRunning: <T>({
		companyId,
		queryOptions
	}: {
		companyId: number;
		queryOptions?: UseQueryOptions<T, HTTPError>;
	}) =>
		useQuery<T, HTTPError, T>(
			'useCheckIsNrlSyncRunning',
			async () => api.get(`AviationObstacles/isNrlSyncRunning?companyId=${companyId}`).json<T>(),
			queryOptions
		),
	useGetAviationObstacleDuplicatesExport: (queryOptions?: UseQueryOptions<Blob, HTTPError>) =>
		useQuery<Blob, HTTPError, Blob>(
			'useGetAviationObstacleDuplicatesExport',
			async () => api.get(`Reports/File/FindDuplicates`).blob(),
			queryOptions
		),
	useGetAviationObstacleQualityExport: (queryOptions?: UseQueryOptions<Blob, HTTPError>) =>
		useQuery<Blob, HTTPError, Blob>(
			'useGetAviationObstacleQualityExport',
			async () => api.get(`Reports/File/QualityReport`).blob(),
			queryOptions
		),

	useSendDatavaskEmailToNrlQuery: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			'useSendDatavaskEmailToNrl',
			() => api.post(`AviationObstacles/SendDatavaskEmailToNrl?useEndpointForDatavask=true`).json(),
			queryOptions
		)
};

const importData = {
	useListPlansFromNisPlanServiceQuery: <T>({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
	} = {}) =>
		useQuery<T, HTTPError, T>(
			['useListPlansFromNisPlanServiceQuery'],
			async () => api.get('Plans/listPlansFromNisPlanService').json<T>(),
			queryOptions
		)
};

const lines = {
	useLineCalculationQuery: <T>({
		queryOptions,
		spanId
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
		spanId: number;
	}) =>
		useQuery<T, HTTPError, T>(
			['useLineCalculationQuery', spanId],
			async () => api.get(`Lines/${spanId}/calculations/data`).json<T>(),
			queryOptions
		),
	useSpanLoadCaseAnalysisQuery: <T>({
		queryOptions,
		spanId
	}: {
		queryOptions?: UseQueryOptions<T, HTTPError>;
		spanId: number;
	}) =>
		useQuery<T, HTTPError, T>(
			['useSpanLoadCaseAnalysisQuery', spanId],
			async () => api.get(`Lines/${spanId}/loadcaseanalysis`).json<T>(),
			queryOptions
		)
};

const lineBaseData = {
	useLineBaseDataQuery: <T>({ queryOptions }: { queryOptions?: UseQueryOptions<T, HTTPError> }) =>
		useQuery<T, HTTPError, T>(
			['useLineBaseDataQuery'],
			async () => await BaseDataService.getLineBaseData(),
			queryOptions
		)
};

const externalLineBaseData = {
	useExternalLineBaseDataQuery: ({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<IExternalLineBaseData[], HTTPError>;
	}) =>
		useQuery<IExternalLineBaseData[], HTTPError, IExternalLineBaseData[]>(
			['useExternalLineBaseDataQuery'],
			async () => await BaseDataService.getExternalLineBaseData(),
			queryOptions
		)
};

const poleBaseData = {
	usePoleBaseDataQuery: ({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<IPoleBaseData[], HTTPError>;
	}) =>
		useQuery<IPoleBaseData[], HTTPError, IPoleBaseData[]>(
			['usePoleBaseDataQuery'],
			async () => await BaseDataService.getPoleBaseData(),
			queryOptions
		)
};

const crossbeam = {
	useGetCrossbeamBaseDataQuery: ({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<IMastpointDataModel.CrossbeamBaseData[], HTTPError>;
	}) =>
		useQuery<
			IMastpointDataModel.CrossbeamBaseData[],
			HTTPError,
			IMastpointDataModel.CrossbeamBaseData[]
		>(
			['useGetCrossbeamBaseDataQuery'],
			async () => api.get(`CrossbeamBaseData`).json<IMastpointDataModel.CrossbeamBaseData[]>(),
			queryOptions
		)
};

const files = {
	useGetPointCloudBaseData: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			['useGetPointCloudBaseData'],
			async () => api.get(`PointCloud`).json<T>(),
			queryOptions
		),
	useFetchPointCloudFiles: <T>(queryOptions?: UseQueryOptions<T, HTTPError>) =>
		useQuery<T, HTTPError, T>(
			['useFetchPointCloudFiles'],
			async () => api.get(`PointCloud/List`).json<T>(),
			queryOptions
		),
	useCreatePointCloudDownloadUrl: ({
		pointCloudId,
		queryOptions
	}: {
		pointCloudId: number;
		queryOptions?: UseQueryOptions<string, HTTPError>;
	}) =>
		useQuery<string, HTTPError, string>(
			['useCreatePointCloudDownloadUrl', pointCloudId],
			async () => api.get(`PointCloud/${pointCloudId}/CreateDownloadUrl`).json<string>(),
			queryOptions
		),
	/**
	 * Images and files attached to a mastpoint, optionally narrowed to one avvik.
	 *
	 * Defaults to the legacy read path, which is correct for every company. Callers
	 * that want compressed previews pass `imagesMigrated` from the company's
	 * `imageMigrationComplete` flag — see `ImagesTab.svelte` for the reactive wiring
	 * needed while that flag is still loading.
	 */
	usefetchMastPointImages: ({
		mastPointId,
		conditionId,
		imagesMigrated = false,
		queryOptions
	}: {
		mastPointId: number;
		conditionId?: number | null;
		imagesMigrated?: boolean;
		queryOptions?: UseQueryOptions<IMastPointImage[], HTTPError>;
	}) =>
		useQuery<IMastPointImage[], HTTPError, IMastPointImage[]>(
			mastPointImagesQueryKey(mastPointId, conditionId),
			() => fetchMastPointImages({ mastPointId, conditionId, imagesMigrated }),
			{ ...mastPointImagesQueryOptions, ...queryOptions }
		),
    useGetFileManagerStatistics: ({
        queryOptions
    }: {
        queryOptions?: UseQueryOptions<ISmartData.FileManagerStatistics, HTTPError>;
    }) => 
        useQuery<ISmartData.FileManagerStatistics, HTTPError, ISmartData.FileManagerStatistics>(
            ['useFileManagerStatistics'],
            async () => api.get('FileManager/statistics').json<ISmartData.FileManagerStatistics>(),
            queryOptions
        ),
	useGetFileManagerFolders: ({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<ISmartData.FileManagerFolderStructure, HTTPError>;
	}) =>
		useQuery<
			ISmartData.FileManagerFolderStructure,
			HTTPError,
			ISmartData.FileManagerFolderStructure
		>(
			['useGetFileManagerFolders'],
			async () => api.get('FileManager/folders').json<ISmartData.FileManagerFolderStructure>(),
			queryOptions
		),
	/** Uploads started but never finalized (crashed/closed tab). */
	useFetchPendingUploads: ({
		queryOptions
	}: {
		queryOptions?: UseQueryOptions<ISmartData.PendingUploadList, HTTPError>;
	} = {}) =>
		useQuery<ISmartData.PendingUploadList, HTTPError, ISmartData.PendingUploadList>(
			['useFetchPendingUploads'],
			() => fetchPendingUploads(),
			queryOptions
		),
	/** Free-text image search (POST `FileManager/Search`). Capped, not paged. */
	useFetchImageSearch: ({
		payload,
		queryOptions
	}: {
		payload: ISmartData.ImageSearchRequest;
		queryOptions?: UseQueryOptions<ISmartData.ImageSearchResult, HTTPError>;
	}) =>
		useQuery<ISmartData.ImageSearchResult, HTTPError, ISmartData.ImageSearchResult>(
			['useFetchImageSearch', payload],
			() => fetchImageSearch(payload),
			queryOptions
		),
	/** Paged/filtered image list (POST). Replaces the deprecated GET `FileManager`. */
	useFetchImageList: ({
		payload,
		queryOptions
	}: {
		payload: ISmartData.FileManagerListRequest;
		queryOptions?: UseQueryOptions<ISmartData.ImageList, HTTPError>;
	}) =>
		useQuery<ISmartData.ImageList, HTTPError, ISmartData.ImageList>(
			['useFetchImageList', payload],
			() => fetchImageList(payload),
			queryOptions
		)
};

const queries = {
	cable,
	crossbeam,
	mastpoint,
	conditions,
	projects,
	companiesAndUsers,
	poles,
	lines,
	lineBaseData,
	externalLineBaseData,
	poleBaseData,
	reports,
	distanceAnalysis,
	aviationObstacleData,
	files,
	utilityLinks,
	importData
};

export { queries };
